import React, { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Megaphone, Plus, X, Edit2, Trash2, Check, Star, Pin, BarChart2 } from 'lucide-react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Staff' },
  { value: 'staff', label: 'Team' },
  { value: 'specific', label: 'Specific People' },
]

export default function AnnouncementsPage() {
  const { user, hasPermission } = useAuth()
  const [searchParams] = useSearchParams()
  const [announcements, setAnnouncements] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<any>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [staffList, setStaffList] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [receipts, setReceipts] = useState<any>(null)
  const [showReceipts, setShowReceipts] = useState(false)
  const [loadingReceipts, setLoadingReceipts] = useState(false)
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<any>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null)
  const [form, setForm] = useState({
    title: '',
    content: '',
    audienceType: 'all',
    specificUserIds: [] as string[],
    isPoll: false,
    pollOptions: ['', ''] as string[],
    isStarAward: false,
    starAwardUserId: ''
  })

  const canPost = hasPermission('post_announcements')

  const fetchAnnouncements = useCallback(async (p = 1) => {
    try {
      const res = await api.get(`/announcements?page=${p}&limit=20`)
      setAnnouncements(res.data.announcements)
      setPagination(res.data.pagination)
    } catch {}
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAnnouncements()
    if (canPost) {
      api.get('/announcements/staff-list').then(res => setStaffList(res.data)).catch(() => {})
    }
  }, [fetchAnnouncements, canPost])

  // Open specific announcement from notification click
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId && announcements.length > 0) {
      const ann = announcements.find(a => a.id === openId)
      if (ann) openAnnouncement(ann)
    }
  }, [searchParams, announcements])

  const openAnnouncement = async (ann: any) => {
    setSelected(ann)
    if (!ann.isRead) {
      try {
        await api.patch(`/announcements/${ann.id}/read`)
        setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, isRead: true } : a))
      } catch {}
    }
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return }
    if (!form.content.trim()) { toast.error('Content is required'); return }
    if (form.audienceType === 'specific' && form.specificUserIds.length === 0) {
      toast.error('Please select at least one recipient')
      return
    }
    setSaving(true)
    try {
      if (editingId) {
        await api.patch(`/announcements/${editingId}`, form)
        toast.success('Announcement updated')
      } else {
        if (form.isStarAward && !form.starAwardUserId) {
          toast.error('Please select a staff member for the Star Award')
          setSaving(false)
          return
        }
        await api.post('/announcements', {
          ...form,
          pollOptions: form.isPoll ? form.pollOptions.filter(o => o.trim()) : []
        })
        toast.success(form.isStarAward ? 'Star Award posted!' : form.isPoll ? 'Poll posted' : 'Announcement posted')
      }
      setShowForm(false)
      setEditingId(null)
      setForm({ title: '', content: '', audienceType: 'all', specificUserIds: [], isPoll: false, pollOptions: ['', ''], isStarAward: false, starAwardUserId: '' })
      fetchAnnouncements()
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const handleEdit = (ann: any) => {
    setEditingId(ann.id)
    setForm({
      title: ann.title,
      content: ann.content,
      audienceType: ann.audienceType,
      specificUserIds: [],
      isPoll: ann.isPoll || false,
      pollOptions: ['', ''],
      isStarAward: false,
      starAwardUserId: ''
    })
    setSelected(null)
    setShowForm(true)
  }

  const REACTION_EMOJIS = ['👍', '❤️', '🔥', '🎉', '👏', '😮']

  const handleReact = async (annId: string, emoji: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const res = await api.post(`/announcements/${annId}/react`, { emoji })
      setAnnouncements(prev => prev.map(a => a.id === annId
        ? { ...a, reactions: res.data.reactions, myReactions: res.data.myReactions }
        : a
      ))
      if (selected?.id === annId) {
        setSelected((prev: any) => prev ? { ...prev, reactions: res.data.reactions, myReactions: res.data.myReactions } : null)
      }
    } catch {}
    setShowEmojiPicker(null)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this announcement?')) return
    try {
      await api.delete(`/announcements/${id}`)
      toast.success('Announcement deleted')
      setSelected(null)
      fetchAnnouncements()
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to delete')
    }
  }

  const toggleSpecificUser = (id: string) => {
    setForm(f => ({
      ...f,
      specificUserIds: f.specificUserIds.includes(id)
        ? f.specificUserIds.filter(uid => uid !== id)
        : [...f.specificUserIds, id]
    }))
  }

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy-900 dark:text-white flex items-center gap-3">
            <Megaphone size={28} className="text-orange-500" />
            Announcements
          </h1>
          <p className="text-gray-400 mt-1 text-sm">Stay updated with team announcements</p>
        </div>
        {canPost && (
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm({ title: '', content: '', audienceType: 'all', specificUserIds: [], isPoll: false, pollOptions: ['', ''], isStarAward: false, starAwardUserId: '' }) }}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-colors text-xs sm:text-sm flex-shrink-0">
            <Plus size={16} /> <span className="hidden sm:inline">New </span>Announcement
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showForm && canPost && (
        <div className="card p-6 space-y-4 border-2 border-orange-200 dark:border-orange-500/20">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>

          {/* Audience */}
          <div>
            <label className="label">To</label>
            <div className="space-y-2">
              {AUDIENCE_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer group">
                  <input type="radio" name="audience" value={opt.value}
                    checked={form.audienceType === opt.value}
                    onChange={() => setForm(f => ({ ...f, audienceType: opt.value }))}
                    className="accent-orange-500" />
                  <span className="text-sm text-navy-700 dark:text-gray-300">{opt.label}</span>
                </label>
              ))}
            </div>

            {/* Specific people picker */}
            {form.audienceType === 'specific' && (
              <div className="mt-3 border border-gray-100 dark:border-[#1e3a5f] rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                {staffList.map(s => (
                  <div key={s.id} onClick={() => toggleSpecificUser(s.id)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 dark:hover:bg-[#1a3050] cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] last:border-0">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      form.specificUserIds.includes(s.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                    }`}>
                      {form.specificUserIds.includes(s.id) && <Check size={12} className="text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-navy-800 dark:text-white">{s.name}</p>
                      <p className="text-xs text-gray-400">{s.roleName}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {form.audienceType === 'specific' && form.specificUserIds.length > 0 && (
              <p className="text-xs text-orange-500 mt-1">{form.specificUserIds.length} people selected</p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="label">Title *</label>
            <input className="input" placeholder="Announcement title..."
              value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>

          {/* Content */}
          <div>
            <label className="label">Content *</label>
            <textarea rows={6} className="w-full border border-gray-200 dark:border-[#1e3a5f] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white"
              placeholder="Write your announcement..."
              value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
          </div>

          {/* Poll toggle */}
          {!editingId && (
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#1a3050] rounded-xl">
              <div>
                <p className="text-sm font-medium text-navy-800 dark:text-white">Make this a Poll</p>
                <p className="text-xs text-gray-400">Staff can vote on options you provide</p>
              </div>
              <button type="button" onClick={() => setForm(f => ({ ...f, isPoll: !f.isPoll }))}
                className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${form.isPoll ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${form.isPoll ? 'left-[3px]' : 'left-[19px]'}`} />
              </button>
            </div>
          )}

          {/* Poll options */}
          {form.isPoll && !editingId && (
            <div className="space-y-2">
              <label className="label">Poll Options *</label>
              {form.pollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className="input flex-1 text-sm" placeholder={`Option ${i + 1}`}
                    value={opt} onChange={e => setForm(f => ({
                      ...f,
                      pollOptions: f.pollOptions.map((o, j) => j === i ? e.target.value : o)
                    }))} />
                  {form.pollOptions.length > 2 && (
                    <button type="button" onClick={() => setForm(f => ({ ...f, pollOptions: f.pollOptions.filter((_, j) => j !== i) }))}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                </div>
              ))}
              {form.pollOptions.length < 5 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, pollOptions: [...f.pollOptions, ''] }))}
                  className="text-xs text-orange-500 hover:text-orange-600 font-medium transition-colors">
                  + Add option
                </button>
              )}
            </div>
          )}

          {/* Star Award toggle */}
          {!editingId && hasPermission('post_star_award') && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-100 dark:border-yellow-500/20">
                <div>
                  <p className="text-sm font-medium text-navy-800 dark:text-white flex items-center gap-1"><Star size={14} className="text-yellow-500" /> Monthly Star Award</p>
                  <p className="text-xs text-gray-400">Recognize a top performer this month</p>
                </div>
                <button type="button" onClick={() => setForm(f => ({ ...f, isStarAward: !f.isStarAward, isPoll: false }))}
                  className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${form.isStarAward ? 'bg-yellow-500' : 'bg-gray-200'}`}>
                  <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${form.isStarAward ? 'left-[3px]' : 'left-[19px]'}`} />
                </button>
              </div>
              {form.isStarAward && (
                <div>
                  <label className="label">Select Star of the Month *</label>
                  <select className="input text-sm" value={form.starAwardUserId}
                    onChange={e => setForm(f => ({ ...f, starAwardUserId: e.target.value }))}>
                    <option value="">— Select staff member —</option>
                    {staffList.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.roleLabel})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors text-sm">
              {saving ? 'Saving...' : editingId ? 'Update Announcement' : 'Post Announcement'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="border border-gray-200 text-gray-500 px-4 py-2.5 rounded-xl hover:bg-gray-50 text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Announcements list */}
      {loading ? (
        <div className="card p-20 text-center text-gray-400">Loading...</div>
      ) : announcements.length === 0 ? (
        <div className="card p-20 text-center">
          <Megaphone size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400">No announcements yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <div key={ann.id}
              onClick={() => openAnnouncement(ann)}
              className={`card p-5 cursor-pointer transition-all hover:border-orange-200 hover:shadow-md relative group ${!ann.isRead ? 'border-l-4 border-l-orange-500 ring-1 ring-orange-300 shadow-[0_0_6px_1px_rgba(251,146,60,0.2)]' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {ann.isPinned && <Pin size={13} className="text-orange-400" />}
                      {!ann.isRead && (
                        <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                      )}
                      <h3 className={`font-display font-semibold text-navy-900 dark:text-white ${!ann.isRead ? 'text-base' : 'text-sm'}`}>
                        {ann.title}
                      </h3>
                    <span className="text-xs bg-gray-100 dark:bg-[#1a3050] text-gray-500 px-2 py-0.5 rounded-full">
                      {AUDIENCE_OPTIONS.find(o => o.value === ann.audienceType)?.label.split(' ').slice(1).join(' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mt-1">{ann.content}</p>
                  {ann.isPoll && (
                    <span className="inline-flex items-center gap-1 text-xs bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full font-medium mt-1">
                      <BarChart2 size={12} className="inline mr-1" />Poll · {ann.poll?.totalVotes || 0} votes
                    </span>
                  )}
                  <div className="flex items-center gap-3 mt-3 text-xs text-gray-400 flex-wrap">
                    <span>By {ann.authorName}</span>
                    <span>·</span>
                    <span>{formatDate(ann.createdAt)}</span>
                    {ann.updatedAt !== ann.createdAt && <span>· Edited</span>}
                    {(ann.isOwn || user?.roleName === 'admin') && ann.recipientCount > 0 && (
                      <span className="text-green-500 font-medium">· 👁 {ann.recipientCount} recipients</span>
                    )}
                  </div>

                  {/* Reactions */}
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap" onClick={e => e.stopPropagation()}>
                    {(ann.reactions || []).map((r: any) => (
                      <button key={r.emoji} onClick={e => handleReact(ann.id, r.emoji, e)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
                          (ann.myReactions || []).includes(r.emoji)
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700 text-orange-600'
                            : 'bg-gray-50 dark:bg-[#162840] border-gray-200 dark:border-[#1e3a5f] text-gray-500 hover:border-orange-200 hover:text-orange-500'
                        }`}>
                        <span>{r.emoji}</span>
                        <span className="font-medium">{r.count}</span>
                      </button>
                    ))}
                    {/* Add reaction button */}
                    <div className="relative">
                      <button onClick={e => { e.stopPropagation(); setShowEmojiPicker(v => v === ann.id ? null : ann.id) }}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-gray-200 dark:border-[#1e3a5f] text-gray-400 hover:border-orange-200 hover:text-orange-500 bg-gray-50 dark:bg-[#162840] transition-colors">
                        <span>+</span><span>😊</span>
                      </button>
                      {showEmojiPicker === ann.id && (
                        <div className="absolute bottom-8 left-0 z-50 bg-white dark:bg-[#0d1e35] border border-gray-100 dark:border-[#1e3a5f] rounded-2xl shadow-xl p-2 flex gap-1">
                          {REACTION_EMOJIS.map(emoji => (
                            <button key={emoji} onClick={e => handleReact(ann.id, emoji, e)}
                              className="text-xl hover:scale-125 transition-transform p-1 rounded-lg hover:bg-gray-50 dark:hover:bg-[#162840]">
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(ann.isOwn || user?.roleName === 'admin') && (
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {hasPermission('post_notices') && (
                      <button onClick={e => {
                        e.stopPropagation()
                        const url = `${window.location.origin}/announcements?open=${ann.id}`
                        navigator.clipboard.writeText(url)
                        toast.success('Link copied!')
                      }} className="p-1.5 text-gray-400 hover:text-orange-500 rounded-lg hover:bg-orange-50 transition-colors" title="Copy link">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      </button>
                    )}
                    <button onClick={async () => {
                      await api.patch(`/announcements/${ann.id}/pin`)
                      fetchAnnouncements()
                    }}
                      className={`p-1.5 rounded-lg transition-colors ${ann.isPinned ? 'text-orange-500 bg-orange-50' : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'}`}
                      title={ann.isPinned ? 'Unpin' : 'Pin'}>
                      <Pin size={14} />
                    </button>
                    <button onClick={() => handleEdit(ann)}
                      className="p-1.5 text-gray-400 hover:text-orange-500 rounded-lg hover:bg-orange-50 transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(ann.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-400">{pagination.total} announcements</p>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => { setPage(p => p - 1); fetchAnnouncements(page - 1) }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <button disabled={page >= pagination.pages} onClick={() => { setPage(p => p + 1); fetchAnnouncements(page + 1) }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Announcement detail modal */}
      {selected && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setSelected(null); setShowReceipts(false); setReceipts(null) }}>
          <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-start justify-between gap-4">
              <div className="flex-1">
                <h2 className="font-display font-bold text-navy-900 dark:text-white text-lg">{selected.title}</h2>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-400 flex-wrap">
                  <span>By {selected.authorName}</span>
                  <span>·</span>
                  <span>{formatDate(selected.createdAt)}</span>
                  {selected.updatedAt !== selected.createdAt && <span>· Edited</span>}
                  <span>·</span>
                  <span>{AUDIENCE_OPTIONS.find(o => o.value === selected.audienceType)?.label}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(selected.isOwn || user?.roleName === 'admin') && (
                  <>
                    <button onClick={async () => {
                      await api.patch(`/announcements/${selected.id}/pin`)
                      fetchAnnouncements()
                      setSelected((prev: any) => prev ? { ...prev, isPinned: !prev.isPinned } : null)
                    }}
                      className={`p-2 rounded-lg transition-colors ${selected.isPinned ? 'text-orange-500 bg-orange-50' : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'}`}>
                      <Pin size={16} />
                    </button>
                    <button onClick={() => handleEdit(selected)}
                      className="p-2 text-gray-400 hover:text-orange-500 rounded-lg hover:bg-orange-50 transition-colors">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(selected.id)}
                      className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
                <button onClick={() => setSelected(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <p className="text-navy-800 dark:text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{selected.content}</p>
              {selected.isPoll && selected.poll && (
                <div className="space-y-2 border border-orange-100 dark:border-orange-700/30 rounded-xl p-4">
                  <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-3">📊 Poll · {selected.poll.totalVotes} votes</p>
                  {selected.poll.options.map((opt: any) => (
                    <div key={opt.id}>
                      <button onClick={async () => {
                        if (selected.poll.userVotedOptionId) return
                        try {
                          const res = await api.post(`/announcements/${selected.id}/vote`, { optionId: opt.id })
                          setSelected((prev: any) => prev ? { ...prev, poll: res.data } : null)
                          setAnnouncements(prev => prev.map(a => a.id === selected.id ? { ...a, poll: res.data } : a))
                        } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to vote') }
                      }}
                        className={`w-full text-left rounded-xl overflow-hidden border transition-all ${
                          selected.poll.userVotedOptionId === opt.id ? 'border-orange-400 dark:border-orange-500' :
                          selected.poll.userVotedOptionId ? 'border-gray-100 dark:border-[#1e3a5f]' :
                          'border-gray-100 dark:border-[#1e3a5f] hover:border-orange-300 cursor-pointer'
                        }`}>
                        <div className="relative px-4 py-2.5">
                          <div className="absolute inset-0 bg-orange-50 dark:bg-orange-900/20 rounded-xl transition-all"
                            style={{ width: selected.poll.userVotedOptionId ? `${opt.percent}%` : '0%' }} />
                          <div className="relative flex items-center justify-between">
                            <span className="text-sm text-navy-800 dark:text-white font-medium">
                              {selected.poll.userVotedOptionId === opt.id && '✓ '}{opt.text}
                            </span>
                            {selected.poll.userVotedOptionId && (
                              <span className="text-xs font-semibold text-orange-500">{opt.percent}%</span>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                  {!selected.poll.userVotedOptionId && (
                    <p className="text-xs text-gray-400 text-center pt-1">Tap an option to vote</p>
                  )}
                </div>
              )}
              {(selected.isOwn || user?.roleName === 'admin') && (
                <div className="border-t border-gray-100 dark:border-[#1e3a5f] pt-4">
                  <button onClick={async () => {
                    if (showReceipts) { setShowReceipts(false); return }
                    setLoadingReceipts(true)
                    try {
                      const res = await api.get(`/announcements/${selected.id}/receipts`)
                      setReceipts(res.data)
                      setShowReceipts(true)
                    } catch {}
                    setLoadingReceipts(false)
                  }} className="flex items-center gap-2 text-xs text-gray-400 hover:text-orange-500 transition-colors font-medium">
                    <span>👁</span>
                    {loadingReceipts ? 'Loading...' : showReceipts ? 'Hide read receipts' : 'Show read receipts'}
                  </button>
                  {showReceipts && receipts && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-gray-400 mb-2">{receipts.readCount} of {receipts.total} have read this</p>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {receipts.recipients.map((r: any) => (
                          <div key={r.userId} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#1a3050]">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.isRead ? 'bg-green-400' : 'bg-gray-300'}`} />
                              <p className="text-xs font-medium text-navy-800 dark:text-white">{r.name}</p>
                              <p className="text-xs text-gray-400">{r.roleName}</p>
                            </div>
                            <p className="text-xs text-gray-400">
                              {r.isRead ? formatDate(r.readAt) : 'Not read'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
